/**
 * MANAGER-OPS-COCKPIT-V2 (V1-P6) -- the PURE tile derivations for the
 * manager ops cockpit (the V1-P4 tracking view re-scoped in place).
 *
 * DERIVATION LAW (charter Scope 2a/2b): every number here is ARITHMETIC over
 * the output of an EXISTING read or an EXISTING shared derivation -- this
 * module computes no new truth:
 *   - day tiles      <- the clinic day board rows (GET /api/appointments/
 *                       today?view=board), statuses stored, signals
 *                       server-derived;
 *   - In consult     <- isInConsultation (ClinicDayBoard.tsx) -- THE one
 *                       predicate the board chip uses (event-first
 *                       consult_started, draft fallback; rider R-07), so the
 *                       tile mirrors the chip by construction;
 *   - cancel reasons <- the stored cancel_reason_code tokens labeled by the
 *                       ONE label module (cancel-reasons.ts); a reason-less
 *                       cancel buckets under the honest "No reason recorded"
 *                       (legacy nulls stay legal -- rider R-08 posture);
 *   - money          <- derivePaymentHandoff / collectQueueSummary
 *                       (payment-handoff.ts) VERBATIM (#247 HANDOFF LAW);
 *                       paid-today is the paymentRecorded EVIDENCE COUNT --
 *                       the board wire deliberately carries no collected
 *                       amount, so no amount is invented here;
 *   - SLA counts     <- the V4-P0 classifier's own items/summary (display
 *                       arithmetic only -- never a second classifier).
 *
 * PHI: counts, controlled tokens, and clinic labels only -- board rows never
 * leave this module; no name/phone/clinical field is emitted.
 */

import type { TodayAppointment } from '@/dashboard/types';
import { isInConsultation } from '@/dashboard/appointments/ClinicDayBoard';
import {
  collectQueueSummary,
  type CollectQueueSummary,
} from '@/dashboard/appointments/payment-handoff';
import { cancelReasonLabel } from '@/dashboard/appointments/cancel-reasons';
import {
  SLA_CLASS_ORDER,
  type SlaBreachClass,
  type SlaLeakageResult,
  type SlaSeverity,
} from '@/dashboard/sla-leakage/sla-leakage-view';

/** The honest bucket label for a reason-less cancel (legacy/pre-apply rows
 *  and the lawful API-optional path -- never hidden, never guessed). */
export const NO_REASON_RECORDED_LABEL = 'No reason recorded';

export interface CancelReasonBreakdownRow {
  /** The stored controlled token, or null = the reason-less bucket. */
  readonly code: string | null;
  readonly label: string;
  readonly count: number;
}

/**
 * EXISTING-SCREEN-COMPLETIONS-V1 (tenant tracker, COC home dashboard
 * "Patients Expected"): the PRE-ARRIVAL stored statuses -- the same
 * vocabulary the doctor day model's Expected band documents
 * (doctor-visit-state.ts: booked / confirmed / rescheduled / waitlist,
 * "not here yet"), restated here as a stored-status set because this
 * module is a DESK-plane tally (PLANE LAW: desk surfaces count the stored
 * status; the doctor surface renders the state machine). Deliberately NOT
 * the stage clock's pre-arrival list (that list is the clock's own and
 * excludes rescheduled for its own reason).
 */
export const DAY_EXPECTED_STATUSES: readonly string[] = [
  'waitlist',
  'booked',
  'confirmed',
  'rescheduled',
];

/** The per-scope day command tiles (one clinic, or the org sum). */
export interface DayCommandTiles {
  /** The day's full slate: every appointment scheduled today (all stored
   *  statuses -- the board read's own IST window). */
  readonly bookedToday: number;
  /** Pre-arrival rows (DAY_EXPECTED_STATUSES): booked for the day and not
   *  in the clinic yet. A NOW-shaped fact -- callers render it for today
   *  only (on a past day a still-pre-arrival row means nobody came). */
  readonly expected: number;
  readonly arrived: number;
  readonly checkedIn: number;
  /** Event-derived (consult_started) with the draft fallback -- THE board
   *  chip predicate (isInConsultation). */
  readonly inConsult: number;
  /** Visits done today: stored fulfilled + completed. */
  readonly fulfilled: number;
  readonly cancelled: number;
  /** Reason breakdown over the cancelled rows, count desc (stable by first
   *  appearance on ties); empty when nothing is cancelled. */
  readonly cancelReasons: readonly CancelReasonBreakdownRow[];
  /** Rows with recorded payment EVIDENCE (paymentRecorded) -- a count, never
   *  an invented amount (the wire carries none). */
  readonly paidTodayCount: number;
  /** The #247 to-collect glance, verbatim. */
  readonly collect: CollectQueueSummary;
}

/** Derive one scope's tiles from its board rows (pure; see module header). */
export function deriveDayCommandTiles(
  rows: readonly TodayAppointment[],
): DayCommandTiles {
  let expected = 0;
  let arrived = 0;
  let checkedIn = 0;
  let inConsult = 0;
  let fulfilled = 0;
  let cancelled = 0;
  let paidTodayCount = 0;
  const reasonCounts = new Map<string | null, number>();

  for (const row of rows) {
    if (DAY_EXPECTED_STATUSES.includes(row.status)) expected += 1;
    if (row.status === 'arrived') arrived += 1;
    if (row.status === 'checked_in') checkedIn += 1;
    if (isInConsultation(row)) inConsult += 1;
    if (row.status === 'fulfilled' || row.status === 'completed') fulfilled += 1;
    if (row.status === 'cancelled') {
      cancelled += 1;
      const code =
        typeof row.cancelReasonCode === 'string' && row.cancelReasonCode !== ''
          ? row.cancelReasonCode
          : null;
      reasonCounts.set(code, (reasonCounts.get(code) ?? 0) + 1);
    }
    if (row.paymentRecorded === true) paidTodayCount += 1;
  }

  const cancelReasons: CancelReasonBreakdownRow[] = [...reasonCounts.entries()]
    .map(([code, count]) => ({
      code,
      label: code !== null ? cancelReasonLabel(code) : NO_REASON_RECORDED_LABEL,
      count,
    }))
    .sort((a, b) => b.count - a.count);

  return {
    bookedToday: rows.length,
    expected,
    arrived,
    checkedIn,
    inConsult,
    fulfilled,
    cancelled,
    cancelReasons,
    paidTodayCount,
    collect: collectQueueSummary(rows),
  };
}

/** Sum per-clinic tiles into the org view (pure arithmetic over the SAME
 *  derivations -- the All-clinics totals can never disagree with the
 *  per-clinic rows they sum). */
export function sumDayCommandTiles(
  perClinic: readonly DayCommandTiles[],
): DayCommandTiles {
  const reasonTotals = new Map<string | null, { label: string; count: number }>();
  let bookedToday = 0;
  let expected = 0;
  let arrived = 0;
  let checkedIn = 0;
  let inConsult = 0;
  let fulfilled = 0;
  let cancelled = 0;
  let paidTodayCount = 0;
  let collectCount = 0;
  let collectAmount: number | null = null;

  for (const t of perClinic) {
    bookedToday += t.bookedToday;
    expected += t.expected;
    arrived += t.arrived;
    checkedIn += t.checkedIn;
    inConsult += t.inConsult;
    fulfilled += t.fulfilled;
    cancelled += t.cancelled;
    paidTodayCount += t.paidTodayCount;
    collectCount += t.collect.count;
    if (t.collect.totalAmount !== null) {
      collectAmount = (collectAmount ?? 0) + t.collect.totalAmount;
    }
    for (const r of t.cancelReasons) {
      const existing = reasonTotals.get(r.code);
      if (existing !== undefined) {
        existing.count += r.count;
      } else {
        reasonTotals.set(r.code, { label: r.label, count: r.count });
      }
    }
  }

  return {
    bookedToday,
    expected,
    arrived,
    checkedIn,
    inConsult,
    fulfilled,
    cancelled,
    cancelReasons: [...reasonTotals.entries()]
      .map(([code, { label, count }]) => ({ code, label, count }))
      .sort((a, b) => b.count - a.count),
    paidTodayCount,
    collect: { count: collectCount, totalAmount: collectAmount },
  };
}

// ---- SLA leakage counts by class, scoped (Scope 1 seam consumption) --------

export interface SlaClassCount {
  readonly breachClass: SlaBreachClass;
  readonly count: number;
  readonly severity: SlaSeverity;
}

/**
 * Class counts for the cockpit's leakage row. Org scope (clinicId null) uses
 * the route's own summary VERBATIM; a clinic narrowing counts the classified
 * ITEMS for that clinic PLUS the clinic-less (org-pool / pending-clinic)
 * items -- pool work belongs to every clinic's manager, and dropping it
 * would let a narrowed tile read 0 while the leakage section it drills to
 * lists the pool rows (adversarial-review finding 1). Display arithmetic
 * over the ONE classifier's output, never a re-classification (severity per
 * class mirrors the engine's summary rule: red when any item is red, else
 * amber, neutral at zero).
 */
export function slaClassCountsForScope(
  view: SlaLeakageResult,
  clinicId: number | null,
): readonly SlaClassCount[] {
  if (clinicId === null) {
    return SLA_CLASS_ORDER.map((breachClass) => {
      const s = view.summary.find((row) => row.breachClass === breachClass);
      return {
        breachClass,
        count: s?.count ?? 0,
        severity: s?.severity ?? 'neutral',
      };
    });
  }
  return SLA_CLASS_ORDER.map((breachClass) => {
    const items = view.items.filter(
      (i) =>
        i.breachClass === breachClass &&
        (i.clinicId === clinicId || i.clinicId === null),
    );
    const severity: SlaSeverity =
      items.length === 0
        ? 'neutral'
        : items.some((i) => i.severity === 'red')
          ? 'red'
          : 'amber';
    return { breachClass, count: items.length, severity };
  });
}

/**
 * Unassigned-past-escalation for the cockpit's scope, HONESTY-GATED
 * (adversarial-review finding 2): when the tracking read is absent OR its
 * ownership overlay is off (matrix.ownershipAvailable false -- breach is
 * not derivable and totalBreaches/breach read 0 structurally), the tile
 * must render its explicit unavailable state, never a fake healthy zero.
 * A clinic ABSENT from the matrix with the overlay ON is a TRUE zero (no
 * open intake). Pure; mirrors the V1-P4 panel's own overlay-off honesty.
 */
export function escalationForScope(
  tracking: {
    readonly totalBreaches: number;
    readonly matrix: {
      readonly ownershipAvailable: boolean;
      readonly clinics: readonly { clinicId: number; breach: number }[];
    };
  } | null,
  clinicId: number | null,
): number | null {
  if (tracking === null || !tracking.matrix.ownershipAvailable) return null;
  if (clinicId === null) return tracking.totalBreaches;
  return tracking.matrix.clinics.find((c) => c.clinicId === clinicId)?.breach ?? 0;
}

/** Whole-INR display (the payment-handoff idiom: en-IN grouping). */
export function inrLabel(amount: number): string {
  return `Rs ${amount.toLocaleString('en-IN')}`;
}
